import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { isEmpty as _isEmpty } from 'lodash';
import othhjs from '@ghs/othhjs';

import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';

import UserIcon from '@mui/icons-material/AccountCircle';
import CloseIcon from '@mui/icons-material/Cancel';
import HelpIcon from '@mui/icons-material/Help';
import InfoIcon from '@mui/icons-material/Info';
import DashboardIcon from '@mui/icons-material/Speed';

import Auth, { ProfileContext, User } from '../data/auth-service';
import SuricataService from '../data/suricata-service';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        height: '85px',
    },
    brand: {
        cursor: 'pointer',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        background: theme.palette.nav.topNav,
        paddingTop: 0,
        paddingBottom: 0,
        height: 80,
    },
    flexBox: {
        flexGrow: 1,
        display: 'flex',
    },
    logo: {
        height: 57,
        marginTop: 10,
        marginLeft: -6,
        marginRight: -25,
    },
    links: {
        color: theme.palette.nav.text,
        fontSize: '22px',
    },
    divider: {
        width: 1,
        height: 80,
        background: `${alpha(theme.palette.nav.text, 0.15)}`,
        marginLeft: 47,
        marginTop: 0,
        paddingTop: 0,
    },
    tightDivider: {
        width: 1,
        height: 80,
        background: `${alpha(theme.palette.nav.text, 0.15)}`,
        marginLeft: theme.spacing(3),
        marginRight: theme.spacing(2),
    },
    menuButton: {
        marginTop: 0,
        color: `${theme.palette.nav.text} !important`,
    },
    alignRight: {
        marginLeft: 'auto'
    },
    whiteColor: {
        color: theme.palette.common.white,
    },
    primaryColor: {
        color: theme.palette.nav.primary,
    },
    secondaryColor: {
        color: theme.palette.secondary.main,
    },
    mc2Link: {
        fontSize: theme.typography.body1.fontSize,
    },
    tooltip: {
        color: theme.palette.common.white,
    },
}));

const TopNav = (props) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [home, setHome] = useState('/');
    const user = User();
    const { getProfile, updateProfile } = useContext(ProfileContext);
    const profileHome = getProfile('home');
    const [managed, setManaged] = useState({ managed: false, mc2: '' });
    const perms = {
        dashboards: Auth.permissions.get('Dashboards'),
        alerts: Auth.permissions.get('Alerts'),
        metadata: Auth.permissions.get('Metadata'),
        sensors: Auth.permissions.get('Sensors'),
        users: Auth.permissions.get('Users'),
        roles: Auth.permissions.get('Roles'),
        audit: Auth.permissions.get('Audit')
    };

    const LSCHECK = process.env.BRICATA_APPLIANCE_TYPE === 'ls';
    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';
    const [url] = useState(othhjs().buildHelpUrl('en', {
        preserve: true,
        documents: [
            {
                module: 'isndr-h-ugd',
                version: '260200',
                topic: 'top-of-doc'
            },
            {
                module: 'isndr-h-agd',
                version: '260200',
                topic: 'top-of-doc'
            },
            {
                module: 'isndr-h-pgd',
                version: '260200',
                topic: 'top-of-doc'
            },
            {
                module: 'isndr-h-pre',
                version: '260200',
                topic: 'top-of-doc'
            },
        ]
    },
        {
            type: 'ofh1',
            tenant: '1234',
            urlRoot: process.env.NODE_ENV === 'development'
                ? 'http://docsapi.staging.opentext.com/mapperpi'
                : 'http://docsapi.opentext.com/mapperpi',
        }));

    const {
        BRICATA_VERSION
    } = process.env;

    useEffect(() => {
        let h = '/';
        const althome = '/support';
        if (_isEmpty(profileHome)) {
            h = '/dashboard';
            updateProfile('home', h);
        }
        if (profileHome) {
            h = profileHome;
            switch (profileHome) {
                case '/dashboard':
                    if (!perms.dashboards.read) h = althome;
                    break;
                case '/views/alerts':
                case '/alerts':
                    if (!perms.alerts.read) h = althome;
                    else h = '/views/alerts';
                    break;
                case '/views/metadata':
                case '/metadata':
                    if (!perms.metadata.read) h = althome;
                    else h = '/views/metadata';
                    break;
                case '/sensors':
                    if (!perms.sensors.read) h = althome;
                    else h = '/sensors';
                    break;
                case '/system/users_roles':
                case '/users':
                    if (!(perms.users.read || perms.roles.read)) h = althome;
                    else h = '/system/users_roles';
                    break;
                case '/system/audit':
                case '/audit':
                    if (!perms.audit.read) h = althome;
                    else h = '/system/audit';
                    break;
            }
        }

        setHome(h);
        if (!MC2CHECK && !LSCHECK) {
            getManaged();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileHome]);

    const getManaged = () => {
        SuricataService.isManaged().then(res => {
            if (res) {
                setManaged(res);
            }
        }, err => {
            console.error(err);
        });
    };

    const handleLink = (link, external) => {
        if (external) {
            window.open(link);
        } else {
            navigate(link);
        }
    };

    const handleLogout = () => {
        Auth.logout();
        navigate('signin');
    };

    return (
        <div className={classes.root} aria-label="Top Navigation Bar">
            <AppBar position="fixed" className={classes.appBar} style={props.header ? { top: '30px' } : {}}>
                <Toolbar>
                    <Box className={classes.flexBox}>
                        <Typography className={classes.brand} onClick={() => { window.open(window.location.href, '_blank','noopener,noreferrer') }}>
                            {/* <img className={classes.logo} src={theme.palette.mode === 'light' ? '/images/logo-new-light.png' : '/images/logo-new.png'} aria-label="Open Default Page"/> */}
                            {/* <img className={classes.logo} src='/images/logo-new.png' aria-label="Open Default Page"/> */}
                            <img
                                className={classes.logo}
                                src={`/images/logos/Top_Nav_CE${BRICATA_VERSION.split('.').length === 3 ? BRICATA_VERSION.split('.').slice(0, 2).join('.') : BRICATA_VERSION}_white.png`}
                                aria-label="Duplicate Page in New Tab"
                            />
                        </Typography>
                        {/* <Box className={classes.divider}></Box> */}
                    </Box>
                    {managed.managed && <Box mr={2}>
                        <Tooltip classes={{ tooltip: classes.tooltip }} title="This CMC is managed by an MC2.">
                            <Link className={classes.mc2Link} color="primary">MC2</Link>
                            {/* <Link className={classes.mc2Link} target="_blank" color="primary" href={`https://${managed.mc2}`}>MC2</Link> */}
                        </Tooltip>
                    </Box>}
                    {perms.dashboards.read &&
                        <Button className={classes.menuButton} onClick={() => { handleLink('/') }} aria-label="Open Dashboard">
                            <Box pt={1} pr={2}><DashboardIcon /></Box>
                            Dashboard
                        </Button>
                    }
                    {!LSCHECK && <Button className={classes.menuButton} aria-label="Open Support" onClick={() => { handleLink('/support') }}>
                        <Box pt={1} pr={2}><InfoIcon /></Box>
                        Support
                    </Button>}
                    {!LSCHECK && <Button className={classes.menuButton} aria-label="Open Help" onClick={() => { handleLink(url, true) }}>
                        <Box pt={1} pr={2}><HelpIcon /></Box>
                        Help
                    </Button>}
                    <Box className={classes.tightDivider}></Box>
                    <Button style={{ textTransform: 'none' }} className={classes.menuButton} aria-label="Update User" onClick={() => { handleLink((LSCHECK ? '/users/update_user/' : '/system/users_roles/update_user/') + user.username) }}>
                        <Box pt={1} pr={2}><UserIcon className={classes.primaryColor} /></Box>
                        Hello, {user.name || user.username}
                    </Button>
                    <Box className={classes.tightDivider}></Box>
                    <Button className={classes.menuButton} onClick={handleLogout} aria-label="Logout">
                        <Box pt={1} pr={2}><CloseIcon className={classes.secondaryColor} /></Box>
                        Logout
                    </Button>
                </Toolbar>

            </AppBar>
        </div>
    );
};
TopNav.propTypes = {
    header: PropTypes.bool,
};

export default TopNav;
